/**
 * geoLevels.js — single source of truth for the map's geographic levels.
 *
 * Before this file every level branch was written inline as
 * `geoLevel === "ward" ? "WD24CD" : "PCON24CD"`, which silently treats any
 * third level as a constituency. Adding local authorities meant either
 * repeating that ternary a third time in ~20 places or naming the per-level
 * facts once. This is the once.
 *
 * A level is described by:
 *   idKey    — the code property carried by its shapefile features, and the
 *              value the backend returns as `area_cd`
 *   nameKey  — the display-name property on the same features
 *   label    — the geo-level pill in the toolbar
 *   noun     — lowercase, for prose ("Find local authority…")
 *   geometry — geometry file under frontend/public. Quantized TopoJSON,
 *              generated by scripts/build-geo.mjs — see that file for why.
 *   parents  — the levels it drills down FROM. Wards nest inside both
 *              constituencies and local authorities, so it is a list and
 *              a ward feature carries a code for each. Neither parent nests in
 *              anything, so both are top levels: a view of the whole country
 *              that you drill down out of.
 */

import { geoAssetUrl } from "./geoAssets";

export const LEVEL_CONSTITUENCY = "constituency";
export const LEVEL_WARD = "ward";
export const LEVEL_LOCAL_AUTHORITY = "local_authority";

const LEVELS = {
  [LEVEL_CONSTITUENCY]: {
    idKey: "PCON24CD",
    nameKey: "PCON24NM",
    label: "Constituency",
    noun: "constituency",
    geometry: "/ukmaps/2024_constituency.topo.json",
    parents: [],
  },
  [LEVEL_WARD]: {
    idKey: "WD24CD",
    nameKey: "WD24NM",
    label: "Ward",
    noun: "ward",
    geometry: "/ukmaps/2024_ward_withPCON.topo.json",
    parents: [LEVEL_CONSTITUENCY, LEVEL_LOCAL_AUTHORITY],
  },
  [LEVEL_LOCAL_AUTHORITY]: {
    idKey: "LAD23CD",
    nameKey: "LAD23NM",
    label: "Local authority",
    noun: "local authority",
    geometry: "/ukmaps/2023_local_authority.topo.json",
    parents: [],
  },
};

export const isGeoLevel = (level) => Object.hasOwn(LEVELS, level);

/** The feature property holding this level's area code. */
export const levelIdKey = (level) => LEVELS[level]?.idKey ?? "area_cd";

/** The feature property holding this level's area name. */
export const levelNameKey = (level) => LEVELS[level]?.nameKey ?? "NAME";

export const levelLabel = (level) => LEVELS[level]?.label ?? level;

export const levelNoun = (level) => LEVELS[level]?.noun ?? "area";

export const levelGeometryUrl = (level) =>
  LEVELS[level]?.geometry ? geoAssetUrl(LEVELS[level].geometry) : null;

/**
 * True for levels that show the whole country at once and carry no parent area.
 * Switching into one resets the selection, and clicking an area drills down
 * into its wards.
 */
export const isTopLevel = (level) => isGeoLevel(level) && !LEVELS[level].parents.length;

/** The levels `level` can be drilled into from. */
export const parentLevels = (level) => LEVELS[level]?.parents ?? [];

// ── Which levels a schema actually has ───────────────────────────────────────
// Every schema mirrors `constituency` and `ward`; only some also stratify to
// local authority. The Parquet mirror is the authority (a schema has the level
// iff <root>/<schema>/local_authority/ exists) but the frontend can't see the
// mirror, so the list is repeated here. Keep it in step with
// LOCAL_AUTHORITY_SCHEMAS in core/views.py.

export const LOCAL_AUTHORITY_SCHEMAS = new Set(["mccain"]);

/** Does this schema have a local_authority table to switch to? */
export const hasLocalAuthorityLevel = (schema) =>
  LOCAL_AUTHORITY_SCHEMAS.has(schema);

/** The geo levels offered for a schema, in toolbar order. */
export const levelsForSchema = (schema) => [
  LEVEL_CONSTITUENCY,
  LEVEL_WARD,
  ...(hasLocalAuthorityLevel(schema) ? [LEVEL_LOCAL_AUTHORITY] : []),
];

// ── Local-authority code vintage ─────────────────────────────────────────────
// The boundary file is ONS "Local Authority Districts (December 2023) UK BFE",
// but the stratified data keys six councils on their PRE-merge GSS codes: the
// 2018 Scottish reissue (Fife, Perth and Kinross), the 2019 one (Glasgow City,
// North Lanarkshire), and the 2023 Barnsley/Sheffield boundary order. The
// geometry is unchanged in every case — only the code was reissued — so the
// join is safe, but joining on the December 2023 code alone would silently
// drop six areas off the map.
//
// Mapped shapefile code → data code, applied in loadGeo so the picker, the map
// layers and the API requests all speak the codes the data uses. Verified by
// resolving each data code's PCON24CDs back to constituency names (E08000038 →
// the three Barnsley seats, E08000039 → the six Sheffield seats, etc.).
//
// If the mirror is ever re-exported on 2023 codes, delete this map.
const LA_CODE_ALIASES = {
  E08000016: "E08000038", // Barnsley
  E08000019: "E08000039", // Sheffield
  S12000047: "S12000015", // Fife
  S12000048: "S12000024", // Perth and Kinross
  S12000049: "S12000046", // Glasgow City
  S12000050: "S12000044", // North Lanarkshire
};

/**
 * Resolve an ONS local-authority code to the code the survey data uses.
 *
 * Every comparison between a district code from geometry and one from the API
 * goes through here — the local-authority layer's own code, and the LAD23CD a
 * ward carries — so both sides of the ward↔district join resolve the same way.
 * Unknown and non-district codes pass through untouched.
 */
export const resolveLocalAuthorityCode = (code) => {
  if (!code) return code;
  const id = String(code);
  return LA_CODE_ALIASES[id] ?? id;
};
